import { useScrollReveal } from '@/hooks/useScrollReveal';

const BABY_PINK = '#d4587c';
const LIGHT_PINK = '#f0a0b5';

const pillars = [
  {
    icon: 'ri-shield-check-line',
    title: 'Ethics First, Always',
    description: 'Every engagement is guided by transparency and honesty — no compromises, no shortcuts.',
  },
  {
    icon: 'ri-user-heart-line',
    title: 'People-Centered Approach',
    description: 'We enhance the net worth of human capital, not just fill vacancies on a spreadsheet.',
  },
  {
    icon: 'ri-search-eye-line',
    title: 'Deep Sector Knowledge',
    description: 'Maximum specialization means we understand your industry before we start the search.',
  },
];

export default function AboutSection() {
  const { ref: sectionRef, isVisible } = useScrollReveal(0.1);

  return (
    <section id="about" className="relative bg-background-100 py-24 md:py-32 px-6 md:px-10 lg:px-16">
      <div
        ref={sectionRef}
        className={`max-w-7xl mx-auto transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Section label */}
        <div className="mb-16 md:mb-20">
          <span
            className="inline-block px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase mb-5"
            style={{ backgroundColor: `${BABY_PINK}15`, color: BABY_PINK }}
          >
            Our Story
          </span>
          <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 leading-[1.1] max-w-4xl">
            Built on the Belief That{' '}
            <span className="italic" style={{ color: LIGHT_PINK }}>People Are the Strategy</span>
          </h2>
        </div>

        {/* Content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-start">
          {/* Left - Story text */}
          <div className="space-y-6">
            <div className="relative">
              <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-0">
                <span className="font-heading text-xl font-bold text-foreground-900">JobManifest</span> is an integrated HR services company built on one belief: the right people in the right places change everything. Founded by Late Mrs. Kamna Taneja, we&apos;ve been turning hiring challenges into human-capital wins since 2017.{' '}
                <span className="text-5xl font-heading text-foreground-300/60 select-none leading-none align-middle">&rdquo;</span>
              </p>
            </div>

            <div>
              <p className="text-foreground-500 text-base leading-relaxed">
                We&apos;re not just recruiters. We&apos;re partners in building workforces that outlast trends and turn talent into competitive advantage. Our vision is literal:{' '}
                <span className="font-semibold italic" style={{ color: LIGHT_PINK }}>&ldquo;We transform dreams into manifestation.&rdquo;</span>{' '}
                That&apos;s not just a tagline — it&apos;s the filter for every decision we make.
              </p>
            </div>

            <p className="text-foreground-500 text-base leading-relaxed">
              Our approach is guided strictly by ethics, honesty, and a deep sector specialization that lets us speak your language — whether you&apos;re in tech, manufacturing, FMCG, NGOs, or beyond.
            </p>

            {/* Founder chip */}
            <div
              className="inline-flex items-center gap-3 px-5 py-3 rounded-full border"
              style={{ borderColor: `${BABY_PINK}30`, backgroundColor: `${BABY_PINK}08` }}
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${BABY_PINK}20` }}
              >
                <span className="font-heading font-bold text-sm" style={{ color: BABY_PINK }}>KT</span>
              </div>
              <div>
                <div className="text-foreground-800 text-sm font-semibold">Late Mrs. Kamna Taneja</div>
                <div className="text-foreground-400 text-xs">Founder, Job Manifest · Est. 2017</div>
              </div>
            </div>
          </div>

          {/* Right - Pillars */}
          <div className="flex flex-col gap-4">
            {pillars.map((pillar, idx) => (
              <div
                key={idx}
                className="group flex items-start gap-5 p-6 rounded-2xl bg-background-50 border border-background-200 hover:border-[#d4587c]/40 hover:bg-background-50 transition-all duration-300 cursor-default"
              >
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ backgroundColor: `${BABY_PINK}12` }}
                >
                  <i className={`${pillar.icon} text-lg`} style={{ color: BABY_PINK }}></i>
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-1.5">
                    {pillar.title}
                  </h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">
                    {pillar.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}